import { defineComponent as _defineComponent } from 'vue'
import { Auth0Connection } from '@/utils/Auth0Connection'
import Auth0ConnectionButton from './Auth0ConnectionButton.vue'
import { validateEmail } from '@/utils/validators'
import { computed, ref, watch } from 'vue'
import user from '@/services/user'
import { AuthorizationParams, PopupConfigOptions, PopupLoginOptions } from '@auth0/auth0-spa-js'
import { useAuth0 } from '@/composables/useAuth0'
import { useDisplay } from '@/composables/useDisplay'


export default /*#__PURE__*/_defineComponent({
  __name: 'SalesBotAuthentication',
  emits: ["update:popup"],
  setup(__props, { emit }: { emit: ({
  (e: 'update:popup'): void
}), expose: any, slots: any, attrs: any }) {

const SOCIAL_CONNECTIONS = [
  Auth0Connection.Google,
  Auth0Connection.Apple,
]

const auth0 = useAuth0()
const { smAndUp, smAndDown } = useDisplay()



const email = ref('')
const isLoading = ref(false)

const isEmailValid = computed(() => {
  return email.value && validateEmail(email.value)
})

const checkCustomerEmail = async (): Promise<boolean> => {
  let isExistingUser = false

  if (!isEmailValid.value) {
    return isExistingUser
  }

  isLoading.value = true
  try {
    const { data } = await user.doesExistInAuth0(email.value)
    isExistingUser = !!data?.exists
  } catch (error) {
    console.warn('Error checking customer email:', error)
  } finally {
    isLoading.value = false
  }

  return isExistingUser
}

// This function is pulled from the utils inside the @auth0/auth0-spa-js package.
// It is used to open a popup window that is then passed to the loginWithPopup function.
// This is necessary because if the click handler to open the popup is async, the popup will be blocked in Safari.
// By opening a blank popup immediately, before any async operations, we can avoid this issue.
// Auth0 then takes over from there and opens the appropriate login page in the popup.
const openPopup = (url: string): Window | null => {
  const width = 400
  const height = 600
  const left = window.screenX + (window.innerWidth - width) / 2
  const top = window.screenY + (window.innerHeight - height) / 2

  return window.open(
    url,
    'auth0:authorize:popup',
    `left=${left},top=${top},width=${width},height=${height},resizable,scrollbars=yes,status=1`
  )
}

const continueWithEmail = async (): Promise<void> => {
  if (!isEmailValid.value) {
    return
  }

  const popup = openPopup('')

  try {
    const isExistingUser = await checkCustomerEmail()

    const authorizationParams: AuthorizationParams = {
      prompt: 'login',
      screen_hint: isExistingUser ? 'login' : 'signup',
      login_hint: email.value,
      connection: Auth0Connection.Email.connectionName,
    }

    const options: PopupLoginOptions = {
      authorizationParams,
    }

    const configOptions: PopupConfigOptions = {
      timeoutInSeconds: 120,
      popup,
    }

    await auth0.loginWithPopup(options, configOptions)
  } catch (error) {
    console.error('Error continuing with email:', error)
  } finally {
    if (popup && !popup.closed) {
      popup.close()
    }
  }
}

const isPopupWindowOpen = computed(() => {
  return auth0.isPopupWindowOpen
})

const isProcessingLogin = computed(() => {
  return auth0.isProcessingPopupLogin
})

watch(isPopupWindowOpen, (newVal, oldVal) => {
  if (!newVal && oldVal) {
    emit('update:popup')
  }
})

return { __sfc: true,SOCIAL_CONNECTIONS, auth0, smAndUp, smAndDown, emit, email, isLoading, isEmailValid, checkCustomerEmail, openPopup, continueWithEmail, isPopupWindowOpen, isProcessingLogin, Auth0ConnectionButton }
}

})